import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isManualRefreshInFlight = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('lb-dealeasy-manual-cart-refresh', this.#handleManualRefresh);
    window.addEventListener('lb-dealeasy-manual-cart-refresh', this.#handleManualRefresh);
    document.addEventListener('lb-dealeasy-vdn-did-atc', this.#handleManualRefresh);
    window.addEventListener('lb-dealeasy-vdn-did-atc', this.#handleManualRefresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('lb-dealeasy-manual-cart-refresh', this.#handleManualRefresh);
    window.removeEventListener('lb-dealeasy-manual-cart-refresh', this.#handleManualRefresh);
    document.removeEventListener('lb-dealeasy-vdn-did-atc', this.#handleManualRefresh);
    window.removeEventListener('lb-dealeasy-vdn-did-atc', this.#handleManualRefresh);
  }

  #handleCartAdd = (event) => {
    const suppressAutoOpen = event?.detail?.data?.suppressCartDrawerOpen === true;
    if (suppressAutoOpen) return;

    const isMixMatchPage = Boolean(document.querySelector('mix-match-collection-component'));
    if (isMixMatchPage) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };



  #handleManualRefresh = async () => {
    if (this.#isManualRefreshInFlight) return;

    const isMixMatchPage = Boolean(document.querySelector('mix-match-collection-component'));
    if (isMixMatchPage) return;

    this.#isManualRefreshInFlight = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = new Set();

      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.add(item.dataset.sectionId);
        }
      });

      if (sectionIds.size > 0) {
        const sectionsParam = Array.from(sectionIds).join(',');
        let pageUrl = window.location.origin;
        if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
          pageUrl = `${pageUrl}${window.Shopify.routes.root}`;
        }
        const url = new URL(pageUrl);
        url.searchParams.set('sections', sectionsParam);

        const response = await fetch(url.toString(), { headers: { 'Accept': 'application/json' } });
        const sectionsHtmlById = await response.json();

        Array.from(sectionIds).forEach((sectionId) => {
          const html = sectionsHtmlById?.[sectionId];
          if (html) {
            morphSection(sectionId, html);
          }
        });
      }
      this.open();
    } finally {
      this.#isManualRefreshInFlight = false;
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
